import React, { useState } from "react";
import { Col, Row } from "antd";
import { Card, Icon, Modal, Tooltip } from "pepsico-ds";
import ResinLineChart from "../../../components/Charts/ResinChart/ResinLineChart";
import SpendDonutChart from "../../../components/Charts/SpendAnalyticsChart/SpendDonutChart";
import RadarChartComponent from "../../../components/Charts/ResilincChart/RadarChartComponent";
import SingleSelectDropdown from "../../../components/Dropdown/SingleSelectDropdown";
import DropdownWithIcons from "../../../components/Modal/DropdownWithIcons";
import { SkeletonList } from "../../../components/Skeleton/ResinSkeleton";
import reset_icon from "../../../assets/reset_icon.svg";
import { useSmartCubeSource } from "./SmartCubeSourceContext";
import LegendList from "../../../components/Charts/Legend/LegendList";
import NoData from "../../../components/NoData/NoData";
import SectorDefinitionsModal from "../../../components/Modal/SectorDefinitionsModal";

// SmartCube text widget fields (detail/implication/mitigation_strategy/insight) can
// arrive as full HTML documents (<!DOCTYPE html>...<body>...</body></html>). Only the
// <body> contents are meaningful for display - strip the doctype/head wrapper.
/* istanbul ignore next */
const extractHtmlBody = (html) => {
  if (!html) return "";
  const bodyMatch = /<body[^>]*>([\s\S]*)<\/body>/i.exec(html);
  return bodyMatch ? bodyMatch[1] : html;
};

// Plain-text preview of an HTML string, used for truncated list previews.
/* istanbul ignore next */
const stripHtmlTags = (html) => {
  if (!html) return "";
  return extractHtmlBody(html).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
};

// <title> text from an HTML document, used as the modal heading when no plain-text title is available.
/* istanbul ignore next */
const extractHtmlTitle = (html) => {
  if (!html) return "";
  const titleMatch = /<title[^>]*>([\s\S]*?)<\/title>/i.exec(html);
  return titleMatch ? titleMatch[1].trim() : "";
};

// Common markup used by the Opportunities, Risks, Mega Trends and
// Sustainability Trends boxes: a "Last updated" / "Next update" metadata
// row followed by a list of items. When onItemClick is provided the items
// are clickable (blue, pointer cursor); otherwise they render as plain
// non-interactive text.
const InsightList = ({ data, onItemClick, lastUpdated, nextUpdate, hideTitle=false }) => (
  <>
    <div style={{ fontSize: "14px", color: "#4B4B4B", fontFamily: "Inter", fontWeight: "400"}}>
      <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
        <Icon alt="" icon="schedule" size="small" />
        Data As On: {lastUpdated || "-"}
      </div>
      {/* <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <Icon alt="" icon="schedule" size="small" />
        Next update: {nextUpdate || "-"}
      </div> */}
    </div>
    <div style={{ background: 'var(--color-border-divider)', width: '100%', height: '1px', margin: '8px 0' }} className="divider"></div>
    <div style={{ display: "flex", flexDirection: "column", gap: "10px", maxHeight: "355px", overflowY: "auto", minHeight: "350px" }}>
      {data.map((item, index) => (
        <div
          key={index}
          
          onKeyDown={
            onItemClick
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onItemClick(item,hideTitle);
                  }
                }
              : undefined
          }
          tabIndex={onItemClick ? 0 : -1}
          role={onItemClick ? "button" : undefined}
          onClick={onItemClick ? () => onItemClick(item, hideTitle) : undefined}
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "6px",
            color: onItemClick ? "#0662C4" : "#4B4B4B",
            fontSize: "14px",
            cursor: onItemClick ? "pointer" : "default",
            fontFamily: "Inter",
          }}
        >
          <Icon alt="" icon="keyboard_arrow_right" size="small" />
          <span style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
            {hideTitle ? (
              <>
                {stripHtmlTags(item.title).split(" ").slice(0, 6).join(" ")}
                {stripHtmlTags(item.title).split(" ").length > 6 ? "..." : ""}
              </>
            ) : (
              item.title
            )}
          </span>
        </div>
      ))}
    </div>
  </>
);


const LastUpdatedAndNextUpdate = ({ lastUpdated, nextUpdate, isDataAsOn=false }) => (
  <div style={{ fontSize: "14px", color: "#4B4B4B", fontFamily: "Inter", fontWeight: "400", display: "flex"}}>
    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <Icon alt="" icon="schedule" size="small" />
      {isDataAsOn ? "Data As On: " : "Last updated: "} {lastUpdated || "-"}
    </div>
    {/* <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "16px" }}>
      <Icon alt="" icon="schedule" size="small" />
      Next update: {nextUpdate || "-"}
    </div> */}
  </div>
);

// Sample SmartCube source component. Unlike CMA, Woodmac, SBACCI and S&P
// Global, SmartCube does not have the "Pricing" / "Reports" tabs navigation
// at the top - it renders a single content area directly below the source
// selection dropdown. This is a sample/placeholder screen until SmartCube
// is fully integrated with its own filters, data fetching and reports.
const SmartCubeSource = props => {
  const {
    section,

    // Reports tab content props (shared with SBACCI in the parent)
    isWoodmacDataLoading,
    renderedWoodmacSections, woodmacAccordionSelectedIndex, cmaPdfReportData,
    renderSection,
    pdfUrl
  } = props;

  const {
    isUserDataLoading, 
    smartCubeChartFiltersData, isSmartCubeFilterDataLoading,
    sectorList, sectorSelected, onSectorChange,
    categoryList, categorySelected, onCategoryChange,
    smartCubeFromDates, smartCubeFromDateSelected, onSmartCubeFromDateChange,
    smartCubeToDates, smartCubeToDateSelected, onSmartCubeToDateChange,
    renderSmartCubeFilterDropdown,
    resetButtonHandler,
    countryList, countrySelected, onCountryChange,
    commodityList, commoditySelected, onCommodityChange,
    costDriverList, costDriverSelected, onCostDriverChange,
    costStructureRegionList, costStructureRegionSelected, onCostStructureRegionChange,
    isSmartCubeChartFiltersLoading,
    priceTrendChartData, priceTrendUnit, isSmartCubePriceTrendLoading, priceTrendLastUpdated, priceTrendNextUpdate,
    costDriversChartData, isSmartCubeCostDriversLoading, costDriversLastUpdated, costDriversNextUpdate,
    costStructureBreakdownData, isSmartCubeCostStructureBreakdownLoading, costStructureBreakdownLastUpdated, costStructureBreakdownNextUpdate,
    marketSizeForecastChartData, marketSizeForecastUnit, isSmartCubeMarketSizeForecastLoading, marketSizeForecastCagr, marketSizeForecastLastUpdated, marketSizeForecastNextUpdate,
    categoryRiskChartData, isSmartCubeCategoryRiskLoading, categoryRiskAvg, categoryRiskLastUpdated, categoryRiskNextUpdate,
    opportunitiesData, opportunitiesLastUpdated, opportunitiesNextUpdate,
    risksData, risksLastUpdated, risksNextUpdate,
    megaTrendsData, megaTrendsLastUpdated, megaTrendsNextUpdate,
    sustainabilityTrendsData, sustainabilityTrendsLastUpdated, sustainabilityTrendsNextUpdate,
    isSmartCubeTextWidgetsLoading,
    linechartRef, donutchartRef, costDriversRef, marketSizeRef, categoryRiskRef,
    checkPDFDownloaded,
    showSmartCubeDetailsModal, setShowSmartCubeDetailsModal
  } = useSmartCubeSource();
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [hideModalTitle, setHideModalTitle] = useState(false);

  const handleOpportunityClick = (opportunity, hideTitle) => {
    setSelectedOpportunity(opportunity);
    setHideModalTitle(hideTitle);
  };

  const handleCloseOpportunityModal = () => {
    setSelectedOpportunity(null);
  };

  /* istanbul ignore next */
  const renderDescriptionText = (text) => {
    if (!text) return null;
    return <div dangerouslySetInnerHTML={{ __html: extractHtmlBody(text) }} />;
  };
  return (
    <>
      {section === "filters" && (
        <>
          <Row
            gutter={[5, 5]}
            justify="start"
            style={{ width: "100%", marginTop: "12px", alignItems: "flex-end" }}
          >
            <Col flex="1">
              <div style={{ fontFamily: "Inter", fontSize: "0.75rem", fontWeight: "400", marginBottom: "3px", display: "flex", alignItems: "center", gap: "4px", color: "#3A3A3A" }}>
                SmartCube Sector
                  <span 
                  
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setShowSmartCubeDetailsModal(true);
                      }
                    }}
                    onClick={() => {
                      setShowSmartCubeDetailsModal(true)
                    }
                  }>
                    <Icon alt="" icon="info" size="small"/>
                  </span>  
              </div>
              <SingleSelectDropdown
                className="buy-con-dropdown-modal cma-dropdown risi-dropdown"
                childList={sectorList}
                selectedValue={sectorSelected}
                setSelectedValue={onSectorChange}
              />
            </Col>
            {renderSmartCubeFilterDropdown("SmartCube Category", categoryList, categorySelected, onCategoryChange, { flex: "1" })}
            {renderSmartCubeFilterDropdown("From Date", smartCubeFromDates, smartCubeFromDateSelected, onSmartCubeFromDateChange, { flex: "1" })}
            {renderSmartCubeFilterDropdown("To Date", smartCubeToDates, smartCubeToDateSelected, onSmartCubeToDateChange, { flex: "1" })}
          </Row>

          <Row justify="space-between" style={{ width: "100%" }}>
            <div style={{ fontSize:"14px", fontWeight:"400", fontFamily: "Inter", color: "#4b4b4b", display:"flex", alignItems:"center" }}>
              {smartCubeChartFiltersData?.link &&  (
                <div style={{ display:"flex" }}>Access SmartCube Report: &nbsp;
                  <a href={smartCubeChartFiltersData?.link} target="_blank" rel="noopener noreferrer" style={{ display:"flex",color:"#004895", fontSize:"16px" }}>
                    {sectorSelected?.[0]?.displayText} - {categorySelected?.[0]?.displayText} <Icon alt="" icon="open_in_new" size="small" style={{ marginLeft: "4px" }} />
                  </a>
                </div>
              )}
            </div>
            <button className="reset-button" onClick={resetButtonHandler} style={{ marginRight: '5px' }}>
              <img src={reset_icon} className="reset_icon" alt="Reset Icon" />
              <span>Reset Filters</span>
            </button>
          </Row>
        </>
      )}
      {
        section === "content" && (
          <>
            <Row  justify={"start"} style={{ width: "100%" }}>
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                    <Card style={{ height: "100%", padding: "24px", width: "100%"}}>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "flex-start",
                                position: "relative",
                                width: "100%"
                            }}
                            >
                                <Row gutter={[16, 16]} justify={"start"} style={{ width: "100%", marginTop: "16px" }}>
                                    <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                                      
                                      <div ref={linechartRef}>
                                        <div  className="sba-cci-linechart-section" style={priceTrendChartData.length <= 0 ? { minHeight: "300px" } : { }}>
                                          {(isUserDataLoading || isSmartCubeFilterDataLoading || isSmartCubeChartFiltersLoading || isSmartCubePriceTrendLoading) ? (
                                            <SkeletonList count={10} height="30" />
                                          ) : (
                                            <div
                                                style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: "16px",
                                                width: "100%",
                                                background: "#fff"
                                                }}
                                            >
                                                <div
                                                    className="resin_linechart_header"
                                                    style={{
                                                        width: "100%",
                                                        alignItems: "center",
                                                        textAlign: "left",
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        fontSize: "18px",
                                                        fontWeight: "600",
                                                        color: "#2d2d2d"
                                                    }}
                                                >
                                                  <span>Price Trend</span>
                                                  <span>
                                                    <DropdownWithIcons modalRef={linechartRef} kpiName={"Price Trend"} checkPDFDownloaded={checkPDFDownloaded} />
                                                  </span>
                                                </div>
                                                {/* <LastUpdatedAndNextUpdate lastUpdated={priceTrendLastUpdated} nextUpdate={priceTrendNextUpdate} isDataAsOn={true} /> */}
                                                <Row gutter={[10, 10]} justify="start" style={{ width: "100%" }}>
                                                  <Col xs={24} sm={24} md={4} lg={4} xl={4}>
                                                    <div style={{ fontFamily: "Inter", fontSize: "0.75rem", fontWeight: "400", marginBottom: "3px", color: "#3A3A3A" }}>Country</div>
                                                    <SingleSelectDropdown
                                                      className="buy-con-dropdown-modal cma-dropdown"
                                                      childList={countryList}
                                                      selectedValue={countrySelected}
                                                      setSelectedValue={onCountryChange}
                                                    />
                                                  </Col>
                                                  <Col xs={24} sm={24} md={4} lg={4} xl={4}>
                                                    <div style={{ fontFamily: "Inter", fontSize: "0.75rem", fontWeight: "400", marginBottom: "3px", color: "#3A3A3A" }}>Commodity</div>
                                                    <SingleSelectDropdown
                                                      className="buy-con-dropdown-modal cma-dropdown"
                                                      childList={commodityList}
                                                      selectedValue={commoditySelected}
                                                      setSelectedValue={onCommodityChange}
                                                    />
                                                  </Col>
                                                </Row>
                                                {
                                                  priceTrendChartData && priceTrendChartData.length > 0 ? (
                                                  <ResinLineChart
                                                    rawData={priceTrendChartData.map((d) => ({ yearMonth: d.month, [priceTrendUnit || "Price"]: d.value }))}
                                                    lineChartactualData={priceTrendChartData.map((d) => ({ yearMonth: d.month, [priceTrendUnit || "Price"]: d.value }))}
                                                    risiSeriesLineChartIndex={[{ key: priceTrendUnit || "Price", name: commoditySelected?.[0]?.displayText || priceTrendUnit || "Price", color: "#218DC0" }]}
                                                    useSimpleTooltip={true}
                                                    yAxisLabel={priceTrendUnit || "Price"}
                                                  />
                                                  ): <NoData/>
                                                }
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </Col>
                                </Row>
                        </div>


                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "flex-start",
                                position: "relative",
                                width: "100%"
                            }}
                            >  
                                <Row gutter={[16, 16]} justify={"start"} style={{ width: "100%"}}>
                                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                                    
                                        <div ref={donutchartRef}>
                                            <div className="sba-cci-linechart-section" style={{ maxHeight: "600px", overflowY: "auto", minHeight: "600px" }}>
                                                {(isUserDataLoading || isSmartCubeFilterDataLoading || isSmartCubeChartFiltersLoading || isSmartCubeCostStructureBreakdownLoading) ? (
                                                  <SkeletonList count={10} height="30" />
                                                ) : (
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: "16px",
                                                        width: "100%",
                                                        background: "#fff"
                                                    }}
                                                >
                                                    <div
                                                        className="resin_linechart_header"
                                                        style={{
                                                            width: "100%",
                                                            alignItems: "center",
                                                            textAlign: "left",
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            fontSize: "18px",
                                                            fontWeight: "600",
                                                            color: "#2d2d2d"
                                                        }}
                                                    >
                                                      <span>Cost Structure Breakdown</span>
                                                      <span>
                                                        <DropdownWithIcons modalRef={donutchartRef} kpiName={"Cost Structure Breakdown"} checkPDFDownloaded={checkPDFDownloaded} />
                                                      </span>
                                                    </div>
                                                    <LastUpdatedAndNextUpdate lastUpdated={costStructureBreakdownLastUpdated} nextUpdate={costStructureBreakdownNextUpdate} isDataAsOn={true} />

                                                    <Row gutter={[10, 10]} justify="start" style={{ width: "100%" }}>
                                                      <Col flex="1">
                                                        <div style={{ fontFamily: "Inter", fontSize: "0.75rem", fontWeight: "400", marginBottom: "3px", color: "#3A3A3A" }}>Region</div>
                                                        <SingleSelectDropdown
                                                          className="buy-con-dropdown-modal cma-dropdown"
                                                          childList={costStructureRegionList}
                                                          selectedValue={costStructureRegionSelected}
                                                          setSelectedValue={onCostStructureRegionChange}
                                                        />
                                                      </Col>
                                                    </Row>
                                                      {
                                                        costStructureBreakdownData && costStructureBreakdownData.length > 0 ? (
                                                        <>
                                                          <SpendDonutChart countryData={costStructureBreakdownData} isCategoryForums={true}/>
                                                          <LegendList legends={costStructureBreakdownData} />
                                                        </>
                                                        ): <NoData/>
                                                      }
                                                </div>
                                                )}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                                        <div ref={costDriversRef}>
                                            <div className="sba-cci-linechart-section" style={{ maxHeight: "600px", minHeight: "600px"}}>
                                                {(isUserDataLoading || isSmartCubeFilterDataLoading || isSmartCubeChartFiltersLoading || isSmartCubeCostDriversLoading) ? (
                                                  <SkeletonList count={10} height="30" />
                                                ) : (
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: "16px",
                                                        width: "100%",
                                                        background: "#fff"
                                                    }}
                                                >
                                                    <div
                                                        className="resin_linechart_header"
                                                        style={{
                                                            width: "100%",
                                                            alignItems: "center",
                                                            textAlign: "left",
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            fontSize: "18px",
                                                            fontWeight: "600",
                                                            color: "#2d2d2d"
                                                        }}
                                                    >
                                                      
                                                      <span>Cost Drivers</span>
                                                      <span>
                                                        <DropdownWithIcons modalRef={costDriversRef} kpiName={"Cost Drivers"} checkPDFDownloaded={checkPDFDownloaded} />
                                                      </span>
                                                    </div>
                                                    {/* <LastUpdatedAndNextUpdate lastUpdated={costDriversLastUpdated} nextUpdate={costDriversNextUpdate} isDataAsOn={true} /> */}
                                                    <div style={{ lineHeight: "14px", padding:"7px" }}></div>
                                                    <Row gutter={[10, 10]} justify="start" style={{ width: "100%" }}>
                                                      <Col flex="1">
                                                        <div style={{ fontFamily: "Inter", fontSize: "0.75rem", fontWeight: "400", marginBottom: "3px", color: "#3A3A3A" }}>Cost Driver</div>
                                                        <SingleSelectDropdown
                                                          className="buy-con-dropdown-modal cma-dropdown"
                                                          childList={costDriverList}
                                                          selectedValue={costDriverSelected}
                                                          setSelectedValue={onCostDriverChange}
                                                        />
                                                      </Col>
                                                    </Row>
                                                    
                                                    {
                                                      costDriversChartData && costDriversChartData.length > 0 ? (
                                                      <>
                                                      <ResinLineChart
                                                        rawData={costDriversChartData.map((d) => ({ yearMonth: d.month, [costDriverSelected?.[0]?.displayText || "Value"]: d.value }))}
                                                        lineChartactualData={costDriversChartData.map((d) => ({ yearMonth: d.month, [costDriverSelected?.[0]?.displayText || "Value"]: d.value }))}
                                                        risiSeriesLineChartIndex={[{ key: costDriverSelected?.[0]?.displayText || "Value", color: "#218DC0" }]}
                                                        useSimpleTooltip={true}
                                                      />
                                                      </>
                                                      ): <NoData/>
                                                    }
                                                </div>
                                                )}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                        </div>

                          
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "flex-start",
                                position: "relative",
                                width: "100%"
                            }}
                            >  
                                <Row gutter={[16, 16]} justify={"start"} style={{ width: "100%"}}>
                                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                                        <div ref={marketSizeRef}>
                                            <div className="sba-cci-linechart-section" style={{ minHeight: "537px" }}>
                                                {(isUserDataLoading || isSmartCubeFilterDataLoading || isSmartCubeChartFiltersLoading || isSmartCubeMarketSizeForecastLoading) ? (
                                                  <SkeletonList count={10} height="30" />
                                                ) : (
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: "16px",
                                                        width: "100%",
                                                        background: "#fff"
                                                    }}
                                                >
                                                    <div
                                                        className="resin_linechart_header"
                                                        style={{
                                                            width: "100%",
                                                            alignItems: "center",
                                                            textAlign: "left",
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            fontSize: "18px",
                                                            fontWeight: "600",
                                                            color: "#2d2d2d"
                                                        }}
                                                    >
                                                      <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                        <span>Market Size Forecast</span>
                                                        <div style={{
                                                          display: "flex",
                                                          height: "24px",
                                                          padding: "2px 8px",
                                                          justifyContent: "center",
                                                          alignItems: "center",
                                                          borderRadius: "4px",
                                                          background: "#95D7FE",
                                                          color: "#000000",
                                                          fontSize: "14px",
                                                          fontWeight: "400",
                                                        }}>CAGR {marketSizeForecastCagr}</div>
                                                      </span>
                                                      <span>
                                                        <DropdownWithIcons modalRef={marketSizeRef} kpiName={"Market Size Forecast"} checkPDFDownloaded={checkPDFDownloaded} />
                                                      </span>
                                                    </div>
                                                    <LastUpdatedAndNextUpdate lastUpdated={marketSizeForecastLastUpdated} nextUpdate={marketSizeForecastNextUpdate} isDataAsOn={true} />
                                                    
                                                    
                                                    {
                                                      marketSizeForecastChartData && marketSizeForecastChartData.length > 0 ? (
                                                      <>
                                                        <ResinLineChart
                                                          rawData={marketSizeForecastChartData.map((d) => ({ yearMonth: d.year, [marketSizeForecastUnit || "Market Size"]: d.value }))}
                                                          lineChartactualData={marketSizeForecastChartData.map((d) => ({ yearMonth: d.year, [marketSizeForecastUnit || "Market Size"]: d.value }))}
                                                          risiSeriesLineChartIndex={[{ key: marketSizeForecastUnit || "Market Size", name: "Market Size", color: "#218DC0" }]}
                                                          useSimpleTooltip={true}
                                                          yAxisLabel={marketSizeForecastUnit || "Market Size"}
                                                        />
                                                      </>
                                                      ): <NoData/>
                                                    }
                                                    
                                                    
                                                </div>
                                                )}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                                    
                                        <div ref={categoryRiskRef}>
                                            <div className="sba-cci-linechart-section" style={{ minHeight: "537px" }}>
                                                {(isUserDataLoading || isSmartCubeFilterDataLoading || isSmartCubeChartFiltersLoading || isSmartCubeCategoryRiskLoading) ? (
                                                  <SkeletonList count={10} height="30" />
                                                ) : (
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: "16px",
                                                        width: "100%",
                                                        background: "#fff"
                                                    }}
                                                >
                                                    <div
                                                        className="resin_linechart_header"
                                                        style={{
                                                            width: "100%",
                                                            alignItems: "center",
                                                            textAlign: "left",
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            fontSize: "18px",
                                                            fontWeight: "600",
                                                            color: "#2d2d2d"
                                                        }}
                                                    >
                                                      <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                        <span>Category Risk</span>
                                                        <div style={{
                                                          display: "flex",
                                                          height: "24px",
                                                          padding: "2px 8px",
                                                          justifyContent: "center",
                                                          alignItems: "center",
                                                          borderRadius: "4px",
                                                          background: "#95D7FE",
                                                          color: "#000000",
                                                          fontSize: "14px",
                                                          fontWeight: "400",
                                                        }}>Avg {categoryRiskAvg}</div>
                                                      </span>
                                                      <span>
                                                        <DropdownWithIcons modalRef={categoryRiskRef} kpiName={"Category Risk"} checkPDFDownloaded={checkPDFDownloaded} />
                                                      </span>
                                                    </div>
                                                    <LastUpdatedAndNextUpdate lastUpdated={categoryRiskLastUpdated} nextUpdate={categoryRiskNextUpdate} isDataAsOn={true} />
                                                    {
                                                      categoryRiskChartData && categoryRiskChartData.length > 0 ? (
                                                      <>
                                                        <RadarChartComponent data={categoryRiskChartData} isSimple={true} />
                                                      </>
                                                      ): <NoData/>
                                                    }
                                                    
                                                </div>
                                                )}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                        </div>


                         
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "flex-start",
                                position: "relative",
                                width: "100%"
                            }}
                            >  
                                <Row gutter={[16, 16]} justify={"start"} style={{ width: "100%"}}>
                                    <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                                            <div className="sba-cci-linechart-section">
                                                {(isUserDataLoading || isSmartCubeFilterDataLoading || isSmartCubeChartFiltersLoading  || isSmartCubeTextWidgetsLoading) ? (
                                                  <SkeletonList count={10} height="30" />
                                                ) : (
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: "16px",
                                                        width: "100%",
                                                        background: "#fff"
                                                    }}
                                                >
                                                    <div
                                                        className="resin_linechart_header"
                                                        style={{
                                                            width: "100%",
                                                            alignItems: "center",
                                                            textAlign: "left",
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            fontSize: "18px",
                                                            fontWeight: "600",
                                                            color: "#2d2d2d"
                                                        }}
                                                    >Opportunities</div>
                                                    {
                                                      opportunitiesData && opportunitiesData.length > 0 ? (
                                                      <>
                                                        <InsightList
                                                          data={opportunitiesData}
                                                          onItemClick={handleOpportunityClick}
                                                          lastUpdated={opportunitiesLastUpdated}
                                                          nextUpdate={opportunitiesNextUpdate}
                                                        />
                                                      </>
                                                      ): <NoData/>
                                                    }
                                                </div>
                                                )}
                                            </div>
                                    </Col>
                                    <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                                            <div className="sba-cci-linechart-section">
                                                {(isUserDataLoading || isSmartCubeFilterDataLoading || isSmartCubeChartFiltersLoading  || isSmartCubeTextWidgetsLoading) ? (
                                                  <SkeletonList count={10} height="30" />
                                                ) : (
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: "16px",
                                                        width: "100%",
                                                        background: "#fff"
                                                    }}
                                                >
                                                    <div
                                                        className="resin_linechart_header"
                                                        style={{
                                                            width: "100%",
                                                            alignItems: "center",
                                                            textAlign: "left",
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            fontSize: "18px",
                                                            fontWeight: "600",
                                                            color: "#2d2d2d"
                                                        }}
                                                    >Risks</div>

                                                    {
                                                      risksData && risksData.length > 0 ? (
                                                      <>
                                                        <InsightList
                                                          data={risksData}
                                                          onItemClick={handleOpportunityClick}
                                                          lastUpdated={risksLastUpdated}
                                                          nextUpdate={risksNextUpdate}
                                                        />
                                                      </>
                                                      ): <NoData/>
                                                    }
                                                </div>
                                                )}
                                            </div>
                                    </Col>
                                    <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                                            <div className="sba-cci-linechart-section">
                                                {(isUserDataLoading || isSmartCubeFilterDataLoading || isSmartCubeChartFiltersLoading  || isSmartCubeTextWidgetsLoading) ? (
                                                  <SkeletonList count={10} height="30" />
                                                ) : (
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: "16px",
                                                        width: "100%",
                                                        background: "#fff"
                                                    }}
                                                >
                                                    <div
                                                        className="resin_linechart_header"
                                                        style={{
                                                            width: "100%",
                                                            alignItems: "center",
                                                            textAlign: "left",
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            fontSize: "18px",
                                                            fontWeight: "600",
                                                            color: "#2d2d2d"
                                                        }}
                                                    >Mega Trends</div>
                                                    {
                                                    megaTrendsData && megaTrendsData.length > 0 ? (
                                                      <>
                                                        
                                                      <InsightList
                                                        data={megaTrendsData}
                                                        hideTitle={true}
                                                        onItemClick={handleOpportunityClick}
                                                        lastUpdated={megaTrendsLastUpdated}
                                                        nextUpdate={megaTrendsNextUpdate}
                                                      />
                                                      </>
                                                      ): <NoData/>
                                                    }
                                                </div>
                                                )}
                                            </div>
                                    </Col>
                                    <Col xs={24} sm={24} md={6} lg={6} xl={6}>
                                            <div className="sba-cci-linechart-section">
                                                {(isUserDataLoading || isSmartCubeFilterDataLoading || isSmartCubeChartFiltersLoading  || isSmartCubeTextWidgetsLoading) ? (
                                                  <SkeletonList count={10} height="30" />
                                                ) : (
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: "16px",
                                                        width: "100%",
                                                        background: "#fff"
                                                    }}
                                                >
                                                    <div
                                                        className="resin_linechart_header"
                                                        style={{
                                                            width: "100%",
                                                            alignItems: "center",
                                                            textAlign: "left",
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            fontSize: "18px",
                                                            fontWeight: "600",
                                                            color: "#2d2d2d"
                                                        }}
                                                    >Sustainability Trends</div>
                                                    {
                                                    sustainabilityTrendsData && sustainabilityTrendsData.length > 0 ? (
                                                      <>
                                                        <InsightList
                                                          data={sustainabilityTrendsData}
                                                          hideTitle={true}
                                                          onItemClick={handleOpportunityClick}
                                                          lastUpdated={sustainabilityTrendsLastUpdated}
                                                          nextUpdate={sustainabilityTrendsNextUpdate}
                                                        />
                                                      </>
                                                      ): <NoData/>
                                                    }
                                                </div>
                                                )}
                                            </div>
                                    </Col>
                                </Row>
                        </div>


                    </Card>
                </Col>
            </Row>

            {selectedOpportunity && (
              <Modal
                onCloseModal={handleCloseOpportunityModal}
                primaryButtonProps={{
                  onClick: handleCloseOpportunityModal,
                  size: "medium",
                  text: "Close",
                  variant: "primary",
                }}
                showPrimaryButton={true}
                showLink={false}
                showSecondaryButton={false}
                showTertiaryButton={false}
                showAuxiliaryButton={false}
                showSlot={true}
                showModal={!!selectedOpportunity}
                closeOnOutsideClick={false}
                hideCloseButton={false}
                type="S"
                title={
                  !hideModalTitle ? (
                    <div title={selectedOpportunity?.title}>
                      {selectedOpportunity?.title}
                    </div>
                  ) : (
                    extractHtmlTitle(selectedOpportunity?.title)
                  )
                }
              >
                
                {/* {!hideModalTitle && (
                  <div className="modal-title" style={{ fontWeight: 700 }}>
                    {selectedOpportunity?.title}
                  </div>
                )} */}
                { hideModalTitle && (
                   <div style={{ marginTop: "12px", fontSize: "14px", color: "#2D2D2D", whiteSpace: "pre-wrap" }}>
                    {renderDescriptionText(selectedOpportunity?.title)}
                  </div>
                )}
                <div style={{ marginTop: "12px", fontSize: "14px", color: "#2D2D2D", whiteSpace: "pre-wrap" }}>
                  {Array.isArray(selectedOpportunity?.description)
                    ? selectedOpportunity.description.map((section, index) => (
                        section.text && (
                          <div key={index} style={{ marginBottom: index === selectedOpportunity.description.length - 1 ? 0 : "12px" }}>
                            <span style={{ fontWeight: 700 }}>{section.label}:</span>
                            {renderDescriptionText(section.text)}
                          </div>
                        )
                      ))
                    : renderDescriptionText(selectedOpportunity?.description)}
                </div>
              </Modal>
            )}
          </>
      )}
      <SectorDefinitionsModal showModal={showSmartCubeDetailsModal} onCloseModal={() => setShowSmartCubeDetailsModal(false)} />
    </>
  );
};

export default SmartCubeSource;
